import { SavResponsesBanner } from "@/components/sav/SavClientResponse";
import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useOrg } from "@/context/OrgContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import FloatingAIAssistant from "@/components/FloatingAIAssistant";
import PWAInstaller from "@/components/PWAInstaller";
import HealthAnomaliesDialog from "@/components/HealthAnomaliesDialog";
import OrgSwitcher from "@/components/OrgSwitcher";
import {
  LayoutDashboard,
  FileText,
  Settings,
  Sliders,
  Tag,
  Users,
  UserCog,
  Briefcase,
  Truck,
  Package,
  Wrench,
  Calendar,
  CalendarDays,
  BarChart3,
  DollarSign,
  Upload,
  Shield,
  ShieldCheck,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  LineChart,
  Target,
  Calculator,
  Trophy,
  Bell,
  Mail,
  ClipboardList,
  TrendingUp,
  Sparkles,
  AlertTriangle,
  Building2,
  Crown,
  Receipt,
  Boxes,
  Layers
} from "lucide-react";

const navItems = [
  {
    path: "/", label: "Tableau de bord", icon: LayoutDashboard, module: "dashboard",
    children: [
      { path: "/suivi-ca", label: "Suivi CA", icon: Calculator, module: "finance" },
      { path: "/graphiques", label: "Graphiques", icon: LineChart, module: "analytics" },
      { path: "/objectifs", label: "Objectifs & Primes", icon: Target, module: "analytics" },
      { path: "/rapports", label: "Rapports", icon: BarChart3, module: "analytics" },
      { path: "/rentabilite-intervenants", label: "Rentabilité intervenants", icon: TrendingUp, module: "analytics" },
      { path: "/commissions", label: "Commissions", icon: DollarSign, module: "commissions" },
    ],
  },
  { path: "/administratif", label: "Administratif", icon: ClipboardList, module: "admin_tasks",
    children: [
      { path: "/comptabilite/cut-off", label: "Comptabilité — Cut-off", icon: Calculator, module: "admin_tasks" },
    ],
  },
  {
    path: "/commandes", label: "Commandes", icon: FileText, module: "orders",
    children: [
      { path: "/planning", label: "Planning", icon: Calendar, module: "orders" },
      { path: "/calendrier", label: "Calendrier", icon: CalendarDays, module: "orders" },
    ],
  },
  { path: "/clients", label: "Clients", icon: Users, module: "clients" },
  { path: "/salaries", label: "Salariés", icon: UserCog, module: "employees" },
  { path: "/fiches-salaries", label: "Fiches salariés", icon: ClipboardList, module: "employee_submissions" },
  { path: "/sous-traitants", label: "Sous-traitants", icon: Briefcase, module: "subcontractors" },
  { path: "/fournisseurs", label: "Fournisseurs", icon: Truck, module: "suppliers" },
  { path: "/materiel", label: "Matériel", icon: Package, module: "settings" },
  { path: "/demandes-chantier", label: "Demandes chantier", icon: Wrench, module: "field_requests" },
  { path: "/sav", label: "SAV", icon: Wrench, module: "orders" },
  { path: "/ia", label: "Assistant IA", icon: Sparkles, module: "dashboard" },
  {
    path: "/parametres", label: "Paramètres", icon: Settings, module: "settings",
    children: [
      { path: "/parametres/configuration", label: "Configuration de l'application", icon: Sliders, module: "settings" },
      { path: "/parametres/logistique", label: "Gestion Logistique", icon: Boxes, module: "orders" },
      { path: "/parametres/equivalences", label: "Équivalences produits", icon: Layers, module: "suppliers" },
      { path: "/parametres/categories-fournisseurs", label: "Catégories fournisseurs", icon: Tag, module: "suppliers" },
      { path: "/types", label: "Types de commandes", icon: Settings, module: "settings" },
      { path: "/vendeurs", label: "Vendeurs", icon: Users, module: "sellers" },
      { path: "/import", label: "Import", icon: Upload, module: "settings" },
      { path: "/notifications", label: "Notifications", icon: Bell, module: "settings" },
      { path: "/emails", label: "Emails", icon: Mail, module: "settings" },
      { path: "/import-commandes", label: "Import commandes", icon: Upload, module: "orders" },
      { path: "/import-fournisseurs", label: "Import fournisseurs", icon: Upload, module: "suppliers" },
    ],
  },
];

const adminSubItems = [
  { path: "/equipe-facturation", label: "Équipe & Facturation", icon: Building2, module: "billing" },
  { path: "/facturation", label: "Factures & abonnement", icon: Receipt, module: "billing" },
  { path: "/admin", label: "Utilisateurs", icon: Shield, adminOnly: true },
  { path: "/administration/permissions", label: "Permissions & rôles", icon: ShieldCheck, orgAdminOnly: true },
  { path: "/performance", label: "Performance users", icon: Trophy, adminOnly: true },
  { path: "/super-admin", label: "Super-Admin", icon: Crown, superAdminOnly: true },
];

export const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const { canRead, isOrgAdmin, isSuperAdmin } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const adminPaths = adminSubItems.map(i => i.path);
  const [adminOpen, setAdminOpen] = useState(adminPaths.includes(window.location.pathname));
  // Groups with children (Dashboard, Commandes) — expanded if a child or the parent itself is active
  const [openGroups, setOpenGroups] = useState(() => {
    const path = window.location.pathname;
    return navItems.reduce((acc, item) => {
      if (item.children) {
        acc[item.path] = item.path === path || item.children.some((c) => c.path === path);
      }
      return acc;
    }, {});
  });

  // Pastille doublons + anomalies : refresh toutes les 5 minutes
  const [duplicatesSummary, setDuplicatesSummary] = useState({ orders_duplicates: 0, clients_duplicates: 0 });
  const [healthAnomalies, setHealthAnomalies] = useState(0);
  const [healthOpen, setHealthOpen] = useState(false);
  // Badges fiches salariés / demandes chantier en attente
  const [pendingValidations, setPendingValidations] = useState({
    submissions_total: 0,
    field_requests_pending: 0,
    total: 0,
  });

  // Polling des validations en attente pour les badges sidebar
  useEffect(() => {
    const canSeeSubs = canRead("employee_submissions");
    const canSeeFR = canRead("field_requests");
    if (!canSeeSubs && !canSeeFR) return;
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const res = await api.getDashboardPendingValidations();
        if (cancelled) return;
        setPendingValidations({
          submissions_total: res.data?.submissions?.total || 0,
          field_requests_pending: res.data?.field_requests?.pending || 0,
          total: res.data?.total_pending || 0,
        });
      } catch {
        // silent
      }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 2 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [canRead]);

  // Garde-route : si l'utilisateur arrive sur une page d'un module qu'il ne peut PAS lire,
  // le rediriger vers son 1er onglet accessible (ex: ouvrier qui tomberait sur /).
  useEffect(() => {
    const path = location.pathname;
    // /login et /accept-invite ne passent pas par Layout → pas besoin de les exclure ici.
    // On flatten navItems + adminSubItems pour trouver le module éventuel de la page courante.
    const allItems = [
      ...navItems.flatMap((i) => (i.children ? [i, ...i.children] : [i])),
      ...adminSubItems,
    ];
    const current = allItems.find((i) => i.path === path);
    // Dashboard `/` → module "dashboard"
    const currentModule = current?.module || (path === "/" ? "dashboard" : null);
    if (!currentModule) return; // pas de mapping → laisser passer (ex: /ia, /profile)
    if (canRead(currentModule)) return;
    // Pas autorisé → cherche le 1er nav item accessible
    const firstAccessible = navItems
      .flatMap((i) => (i.children ? i.children : [i]))
      .find((i) => !i.module || canRead(i.module));
    if (firstAccessible && firstAccessible.path !== path) {
      navigate(firstAccessible.path, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, canRead]);
  useEffect(() => {
    // Skip pour les comptes sans accès aux commandes (ouvriers) — évite des reads inutiles + 403
    if (!canRead("orders")) return;
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const res = await api.getHealthSummary();
        if (!cancelled) {
          setDuplicatesSummary(res.data?.duplicates || { orders_duplicates: 0, clients_duplicates: 0 });
          setHealthAnomalies(res.data?.total_anomalies || 0);
        }
      } catch {
        // silent
      }
    };
    fetchSummary();
    const interval = setInterval(fetchSummary, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [canRead]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const NavItem = ({ item, indent = false }) => {
    let badgeCount = 0;
    let badgeColor = "amber"; // "amber" pour doublons, "indigo" pour validations en attente
    let badgeTitle = "";
    if (item.path === "/clients") {
      badgeCount = duplicatesSummary.clients_duplicates || 0;
      badgeTitle = `${badgeCount} doublon(s) détecté(s)`;
    }
    if (item.path === "/commandes") {
      badgeCount = duplicatesSummary.orders_duplicates || 0;
      badgeTitle = `${badgeCount} doublon(s) détecté(s)`;
    }
    if (item.path === "/fiches-salaries") {
      badgeCount = pendingValidations.submissions_total || 0;
      badgeColor = "indigo";
      badgeTitle = `${badgeCount} fiche(s) en attente de validation`;
    }
    if (item.path === "/demandes-chantier") {
      badgeCount = pendingValidations.field_requests_pending || 0;
      badgeColor = "indigo";
      badgeTitle = `${badgeCount} demande(s) en attente`;
    }
    const badgeClass = badgeColor === "indigo"
      ? "bg-indigo-100 text-indigo-800 border-indigo-300"
      : "bg-amber-100 text-amber-800 border-amber-300";
    return (
      <NavLink
        to={item.path}
        end={item.path === "/"}
        onClick={() => setSidebarOpen(false)}
        className={({ isActive }) =>
          `sidebar-link ${isActive ? "active" : ""} ${indent ? "ml-4" : ""}`
        }
        data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
      >
        <item.icon className="h-4 w-4" strokeWidth={1.5} />
        <span className="flex-1">{item.label}</span>
        {badgeCount > 0 && (
          <span
            className={`inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1.5 rounded-full text-[10px] font-bold border ${badgeClass}`}
            title={badgeTitle}
            data-testid={`nav-badge-${item.path.replace("/", "")}`}
          >
            {badgeCount}
          </span>
        )}
      </NavLink>
    );
  };

  const NavGroup = ({ item }) => {
    const isOpen = openGroups[item.path];
    const toggle = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setOpenGroups((g) => ({ ...g, [item.path]: !g[item.path] }));
    };
    let badgeCount = 0;
    if (item.path === "/clients") badgeCount = duplicatesSummary.clients_duplicates || 0;
    if (item.path === "/commandes") badgeCount = duplicatesSummary.orders_duplicates || 0;
    return (
      <div>
        <div className="flex items-center">
          <NavLink
            to={item.path}
            end={item.path === "/"}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `sidebar-link flex-1 ${isActive ? "active" : ""}`}
            data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
          >
            <item.icon className="h-4 w-4" strokeWidth={1.5} />
            <span className="flex-1">{item.label}</span>
            {badgeCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold border border-amber-300"
                title={`${badgeCount} doublon(s) détecté(s)`}
                data-testid={`nav-badge-${item.path.replace("/", "")}`}
              >
                ⚠ {badgeCount}
              </span>
            )}
          </NavLink>
          <button
            type="button"
            onClick={toggle}
            className="p-2 text-muted-foreground hover:text-foreground"
            aria-label={isOpen ? "Replier" : "Déplier"}
            data-testid={`nav-toggle-${item.path.replace("/", "") || "dashboard"}`}
          >
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </div>
        {isOpen && (
          <div className="space-y-0.5 mt-0.5">
            {item.children.map((c) => <NavItem key={c.path} item={c} indent />)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — fixed on mobile (overlay), sticky on desktop so it stays visible while scrolling */}
      <aside
        className={`sidebar fixed lg:sticky top-0 h-screen w-64 flex flex-col z-50 shrink-0 self-start ${
          sidebarOpen ? "open" : ""
        }`}
      >
        {/* Logo + nom de l'application */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-100">
          <div className="flex items-center gap-2 min-w-0">
            <img
              src="/logo-bati-assistant.svg"
              alt="Bati Assistant"
              className="h-9 w-9 shrink-0"
              data-testid="sidebar-logo"
            />
            <h1 className="font-heading font-semibold text-lg text-zinc-900 tracking-tight truncate">
              Bati Assistant
            </h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
          {navItems
            .filter((item) => !item.module || canRead(item.module))
            .map((item) => (
            item.children
              ? <NavGroup key={item.path} item={{
                  ...item,
                  children: item.children.filter(c => !c.module || canRead(c.module)),
                }} />
              : <NavItem key={item.path} item={item} />
          ))}

          {(() => {
            const visibleAdminItems = adminSubItems.filter(i => {
              if (i.adminOnly && !isAdmin) return false;
              if (i.orgAdminOnly && !isOrgAdmin) return false;
              if (i.superAdminOnly && !isSuperAdmin) return false;
              if (i.module && !canRead(i.module)) return false;
              return true;
            });
            return visibleAdminItems.length > 0 ? (
              <div className="pt-3 mt-3 border-t border-border">
                <button
                  onClick={() => setAdminOpen(!adminOpen)}
                  className="w-full flex items-center justify-between px-2.5 py-1.5 mb-0.5 text-[11px] font-medium text-zinc-500 uppercase tracking-wider hover:text-zinc-900 hover:bg-zinc-100 transition-all duration-200 ease-out rounded-lg"
                  data-testid="admin-toggle"
                >
                  <div className="flex items-center gap-2">
                    <Shield className="h-3.5 w-3.5" />
                    <span>Administration</span>
                  </div>
                  {adminOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>
                {adminOpen && (
                  <div className="space-y-0.5">
                    {visibleAdminItems.map((item) => (
                      <NavItem key={item.path} item={item} />
                    ))}
                  </div>
                )}
              </div>
            ) : null;
          })()}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-border">
          <button
            type="button"
            onClick={() => navigate("/mon-compte")}
            className="flex items-center gap-3 mb-3 w-full text-left rounded-xl hover:bg-zinc-100 transition-all duration-200 ease-out p-2 -m-1"
            data-testid="open-account-btn"
            title="Mon compte (changer email / mot de passe)"
          >
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="font-heading font-bold text-primary">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">
                {user?.role === "admin" ? "Administrateur" : "Utilisateur"}
              </p>
            </div>
          </button>
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-h-screen min-w-0 bg-zinc-50">
        {/* Top bar */}
        <header className="sticky top-0 z-40 h-16 bg-zinc-50/80 backdrop-blur-md flex items-center px-4 lg:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden mr-4"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center text-sm text-zinc-400 flex-1 font-medium">
            <span>Bati Assistant</span>
            <ChevronRight className="h-3.5 w-3.5 mx-1.5" />
            <span className="text-zinc-900">
              {navItems.find(i => i.path === window.location.pathname)?.label || 
               adminSubItems.find(i => i.path === window.location.pathname)?.label ||
               ({ "/mon-compte": "Mon compte", "/parametres/configuration": "Configuration" }[window.location.pathname] || "Tableau de bord")}
            </span>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <OrgSwitcher />
            {healthAnomalies > 0 && canRead("orders") && (
              <Button
                variant="outline"
                size="sm"
                className="h-9 border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-800"
                onClick={() => setHealthOpen(true)}
                data-testid="open-health-anomalies"
                title="Anomalies de saisie détectées"
              >
                <AlertTriangle className="h-4 w-4 mr-1.5" />
                <span className="hidden sm:inline">Anomalies</span>
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-100 text-amber-900 text-[11px] font-semibold">
                  {healthAnomalies}
                </span>
              </Button>
            )}
          </div>
        </header>

        <HealthAnomaliesDialog open={healthOpen} onClose={() => setHealthOpen(false)} />

        {/* Page content — full width to reduce empty side margins on wide screens */}
        <main className="px-4 lg:px-6 py-6 lg:py-8 overflow-x-hidden">
          <SavResponsesBanner />
          {children}
        </main>
      </div>

      {/* Global floating AI assistant (hidden on the full /ia page to avoid duplication) */}
      {location.pathname !== "/ia" && <FloatingAIAssistant />}
      <PWAInstaller />
    </div>
  );
};

export default Layout;
